import "@/App.css";
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTopOnRouteChange from "@/components/ScrollToTopOnRouteChange";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import AdvisorsPage from "@/pages/AdvisorsPage";
import AppointmentsPage from "@/pages/AppointmentsPage";
import StatementPage from "@/pages/StatementPage";
import FlyerPage from "@/pages/FlyerPage";
import RoadmapPage from "@/pages/RoadmapPage";
import ContractPage from "@/pages/ContractPage";
import SpecsPage from "@/pages/SpecsPage";
import SegmentsPage from "@/pages/SegmentsPage";
import SmsLogPage from "@/pages/SmsLogPage";
import SettingsPage from "@/pages/SettingsPage";
import CustomerSchedulePage from "@/pages/CustomerSchedulePage";
import CustomerCallPage from "@/pages/CustomerCallPage";
import CampaignsPage from "@/pages/CampaignsPage";
import ApiLetterPage from "@/pages/ApiLetterPage";
import CloudsPage from "@/pages/CloudsPage";
import ROICalculatorPage from "@/pages/ROICalculatorPage";
import ExamplesPage from "@/pages/ExamplesPage";
import OnePagerPage from "@/pages/OnePagerPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import OnboardingPage from "@/pages/OnboardingPage";
import FirstRunWizardPage from "@/pages/FirstRunWizardPage";
import ComebacksPage from "@/pages/ComebacksPage";
import PublicDemoPage from "@/pages/PublicDemoPage";
import MPIPage from "@/pages/MPIPage";
import AdvisorScorecardPage from "@/pages/AdvisorScorecardPage";
import CustomerJourneyPage from "@/pages/CustomerJourneyPage";
import PitchPage from "@/pages/PitchPage";
import ConquestPage from "@/pages/ConquestPage";
import PartsPipelinePage from "@/pages/PartsPipelinePage";
import TechniciansPage from "@/pages/TechniciansPage";
import TechnicianScorecardPage from "@/pages/TechnicianScorecardPage";
import TechnicianHandoutPage from "@/pages/TechnicianHandoutPage";
import KimobyComparePage from "@/pages/KimobyComparePage";
import PayrollPage from "@/pages/PayrollPage";
import DeclineDriveLetterPage from "@/pages/DeclineDriveLetterPage";
import TechDashboardPage from "@/pages/TechDashboardPage";
import BusinessCardPage from "@/pages/BusinessCardPage";
import BodyShopPage from "@/pages/BodyShopPage";
import PreownedPossePage from "@/pages/PreownedPossePage";
import PossePastDuePage from "@/pages/PossePastDuePage";
import ReconRacePage from "@/pages/ReconRacePage";
import ManagerCheckinPage from "@/pages/ManagerCheckinPage";
import PhotoPipelinePage from "@/pages/PhotoPipelinePage";
import SignupPage from "@/pages/SignupPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import PlatformAdminPage from "@/pages/PlatformAdminPage";
import NudgeTemplatesPage from "@/pages/NudgeTemplatesPage";
import LineItemsReportPage from "@/pages/LineItemsReportPage";
import AdvisorSalesScorecardPage from "@/pages/AdvisorSalesScorecardPage";
import MyVehiclePage from "@/pages/MyVehiclePage";
import VideoLibraryPage from "@/pages/VideoLibraryPage";
import { Toaster } from "@/components/ui/sonner";

function LandingRedirect() {
  const { user, loading } = useAuth();

  // Anonymous visitors see the public marketing site at /marketing/index.html.
  // We fetch the HTML and swap it in with document.open/write/close so the
  // address bar stays at "/" instead of visibly navigating to the file path.
  // The HTML uses absolute asset paths (/marketing/logo.jpg, /marketing/promo.mp4,
  // /login) so every reference still resolves against the root origin.
  useEffect(() => {
    if (loading || user) return;
    let cancelled = false;
    fetch("/marketing/index.html", { credentials: "same-origin", cache: "no-cache" })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error("marketing fetch failed"))))
      .then((html) => {
        if (cancelled) return;
        // Replace the entire document with the marketing HTML byte-for-byte;
        // browsers run <style>, <script>, and resource tags inside the written
        // stream, so the page renders identically to a direct hit on the file.
        document.open();
        document.write(html);
        document.close();
      })
      .catch(() => {
        // Fallback: if the fetch is blocked for any reason, hand off with a
        // hard redirect so the user still sees the marketing site.
        if (!cancelled) window.location.replace("/marketing/index.html");
      });
    return () => {
      cancelled = true;
    };
  }, [loading, user]);

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  // Render nothing while the fetch/document.write happens — avoids a flash
  // of the SPA shell before the marketing HTML takes over.
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTopOnRouteChange />
          <Routes>
            <Route path="/" element={<LandingRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            <Route path="/schedule/:declineId" element={<MyVehiclePage />} />
            <Route path="/vehicle/:declineId" element={<MyVehiclePage />} />
            <Route path="/call/:declineId" element={<CustomerCallPage />} />
            <Route path="/demo" element={<PublicDemoPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/advisors"
              element={
                <ProtectedRoute>
                  <AdvisorsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/line-items"
              element={
                <ProtectedRoute>
                  <LineItemsReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales-scorecard"
              element={
                <ProtectedRoute>
                  <AdvisorSalesScorecardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/video-library"
              element={
                <ProtectedRoute>
                  <VideoLibraryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/appointments"
              element={
                <ProtectedRoute>
                  <AppointmentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaigns"
              element={
                <ProtectedRoute>
                  <CampaignsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/statement"
              element={
                <ProtectedRoute>
                  <StatementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/flyer"
              element={
                <ProtectedRoute>
                  <FlyerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/roadmap"
              element={
                <ProtectedRoute>
                  <RoadmapPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contract"
              element={
                <ProtectedRoute>
                  <ContractPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/specs"
              element={
                <ProtectedRoute>
                  <SpecsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/api-letter"
              element={
                <ProtectedRoute>
                  <ApiLetterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clouds"
              element={
                <ProtectedRoute>
                  <CloudsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/roi"
              element={
                <ProtectedRoute>
                  <ROICalculatorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/examples"
              element={
                <ProtectedRoute>
                  <ExamplesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/one-pager"
              element={
                <ProtectedRoute>
                  <OnePagerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/how-it-works"
              element={
                <ProtectedRoute>
                  <HowItWorksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/segments"
              element={
                <ProtectedRoute>
                  <SegmentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sms"
              element={
                <ProtectedRoute>
                  <SmsLogPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/comebacks"
              element={
                <ProtectedRoute>
                  <ComebacksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mpi"
              element={
                <ProtectedRoute>
                  <MPIPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/advisor-scorecard/:advisorId"
              element={
                <ProtectedRoute>
                  <AdvisorScorecardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-journey"
              element={
                <ProtectedRoute>
                  <CustomerJourneyPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pitch"
              element={
                <ProtectedRoute>
                  <PitchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/conquest"
              element={
                <ProtectedRoute>
                  <ConquestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parts"
              element={
                <ProtectedRoute>
                  <PartsPipelinePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/body-shop"
              element={
                <ProtectedRoute>
                  <BodyShopPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/posse"
              element={
                <ProtectedRoute>
                  <PreownedPossePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/posse/past-due"
              element={
                <ProtectedRoute>
                  <PossePastDuePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/recon-recovery"
              element={
                <ProtectedRoute>
                  <PossePastDuePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/recon-race"
              element={
                <ProtectedRoute>
                  <ReconRacePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manager-checkin"
              element={
                <ProtectedRoute>
                  <ManagerCheckinPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/photo-pipeline"
              element={
                <ProtectedRoute>
                  <PhotoPipelinePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business-card"
              element={
                <ProtectedRoute>
                  <BusinessCardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/technicians"
              element={
                <ProtectedRoute>
                  <TechniciansPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/technician-scorecard/:techId"
              element={
                <ProtectedRoute>
                  <TechnicianScorecardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tech-handout"
              element={
                <ProtectedRoute>
                  <TechnicianHandoutPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/kimoby-vs"
              element={
                <ProtectedRoute>
                  <KimobyComparePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll"
              element={
                <ProtectedRoute>
                  <PayrollPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/decline-drive-letter"
              element={
                <ProtectedRoute>
                  <DeclineDriveLetterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/technician-dashboard"
              element={
                <ProtectedRoute>
                  <TechDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/platform"
              element={
                <ProtectedRoute>
                  <PlatformAdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/first-run"
              element={
                <ProtectedRoute>
                  <FirstRunWizardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/platform/nudge-templates"
              element={
                <ProtectedRoute>
                  <NudgeTemplatesPage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<LandingRedirect />} />
          </Routes>
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
